<template>
  <section>
    <v-row class="my-5">
      <v-col cols="12" md="6">
        <v-select
          v-if="deliveryPickUpEventsLoaded"
          v-model="deliveryPickUpEvent"
          :items="deliveryPickUpSelect"
          hide-details="auto"
          outlined
          return-object
          item-value="Name"
          item-text="Name"
          :prepend-inner-icon="isDelivery ? 'mdi-truck' : 'mdi-store'"
          :label="$translate('MAKE_CHOICE_FOR_DELIVERY_PICKUP')"
          single-line
          @change="onDeliveryPickUpChange"
        >
          <template v-slot:item="data">
            <template v-if="!isObject(data.item)">
              <v-list-item-content v-text="data.item" />
            </template>
            <template v-else>
              <v-list-item-avatar>
                <v-icon
                  v-if="data.item.Type === 'Delivery'"
                  color="secondary"
                  large
                >
                  mdi-truck
                </v-icon>
                <v-icon v-else color="secondary" large>mdi-store</v-icon>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title v-html="data.item.Name" />
                <v-list-item-subtitle
                  v-text="getDeliveryPickupSubtitle(data.item)"
                />
              </v-list-item-content>
            </template>
          </template>
        </v-select>

        <!-- Delivery options section -->
        <v-expand-transition>
          <section
            v-if="isDelivery && deliveryFeeItems.length > 0"
            class="mt-2 px-2"
          >
            <!-- Delivery fee option in case of only 1 delivery available -->
            <div
              v-if="deliveryFeeItems.length === 1"
              class="text-subtitle-1 text--primary"
            >
              {{ deliveryFeeItems[0].Name }}
            </div>

            <!-- Delivery fee options in case of more than 1 delivery available -->
            <v-radio-group
              v-else
              v-model="selectedDeliveryFeeItem"
              hide-details="auto"
              column
              class="mt-0"
              @change="onDeliverySelectionChange"
            >
              <v-radio
                v-for="(
                  deliveryFeeItem, deliveryFeeItemIndex
                ) in deliveryFeeItems"
                :key="deliveryFeeItemIndex"
                :value="deliveryFeeItem"
              >
                <template v-slot:label>
                  <div class="text-subtitle-1 text--primary">
                    {{ deliveryFeeItem.Name }}
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </section>
        </v-expand-transition>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import doRequest from '@/api/dssRequest';
import utility from '@/shared/utility';
import PickupFeeJS from '@/objects/PickupFee';

export default {
  name: 'DeliveryPickUpEvent',
  props: {
    // Events that need delivery / pickup services
    eventIds: Array,
    initialSelection: Object,
    initialDeliveryFees: Array,
    // initialDeliveryFeeSelection: Object,

    /**
     * Reservations already created to be used for ShoppingCart flow to check exluded products
     * e.g. { 'event-1': ['order-1', 'order-2'] }
     */
    reservations: {
      type: Object,
      required: false,
    },

    /**
     * Products selected for subscription, used to check excluded products
     * e.g. [{ ProductId: 'xxx', ItemId: 'xxx', Quantity: 1 }]
     */
    subscriptionProducts: {
      type: Array,
      required: false,
    },

    // Used to check excluded products
    shoppingListId: {
      type: String,
      required: false,
    },
  },
  emits: ['setCountryCodes', 'change', 'change:delivery-fee'],
  data() {
    return {
      deliveryPickUpEventsLoaded: false,
      pickUpEvents: [],
      noDelivery: false,
      deliveryPickUpEvent: {},
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      deliveryFeeItems: [],
      selectedDeliveryFeeItem: {},
    };
  },
  computed: {
    userObjectId() {
      return this.$store.getters['auth/isValid']
        ? this.$store.getters['auth/userName']
        : this.$store.getters['auth/tmpUserId'];
    },
    deliveryPickUpSelect() {
      const selectionItems = this.pickUpEvents;
      const hasPickUp =
        this.pickUpEvents != null && this.pickUpEvents.length > 0;
      // add header
      if (hasPickUp) {
        selectionItems.unshift({ header: 'pickup' });
      }

      // if delivery is possible then add option
      if (!this.noDelivery) {
        if (hasPickUp) {
          selectionItems.unshift({ divider: true });
        }
        selectionItems.unshift({
          Name: this.$translate('DELIVERY'),
          Type: 'Delivery',
        });
      }

      return selectionItems;
    },
    isDelivery() {
      if (
        this.deliveryPickUpEvent.Type === 'Delivery' ||
        this.deliveryPickUpEvent.Type === 'Shiplee'
      ) {
        return true;
      }
      return false;
    },
  },
  watch: {
    eventIds() {
      this.getDeliveryAndPickUpEvents();
    },
    initialDeliveryFees() {
      this.initDeliveryFees();
    },
  },
  mounted() {
    this.getDeliveryAndPickUpEvents();
    this.initDeliveryFees();
  },
  methods: {
    getDeliveryAndPickUpEvents() {
      // Retrieving the delivery and pick up Events
      const payload = {
        Events: [],
      };

      if (this.eventIds != null) {
        payload.Events = [...this.eventIds];
      }

      if (this.subscriptionProducts != null) {
        payload.SubscriptionProducts = [...this.subscriptionProducts];
      }

      if (this.reservations != null) {
        payload.Reservations = { ...this.reservations };
      }

      if (this.shoppingListId != null) {
        payload.ShoppingListId = this.shoppingListId;
      }

      this.pickUpEvents = [];

      doRequest(
        (response) => {
          this.pickUpEvents = response.data.PickUpEvents;

          if (response.data.DeliveryEvents.length === 0) {
            this.noDelivery = true;
          }
          // Set default selection
          this.initDeliveryPickUpEvent();

          // to show the colums after the data is received
          this.deliveryPickUpEventsLoaded = true;

          // set countryCodes
          this.$emit('setCountryCodes', response.data.DeliveryCountryCodes);
        },
        () => {
          this.$emit('error', {
            error: true,
            errorMessage: this.$translate('NO_DELIVERY_OR_PICKUP'),
            errorToRecallFunction: 'getDeliveryAndPickUpEvents',
          });
        },
        'getDeliveryAndPickUpEvents',
        this.userObjectId,
        payload
      );
    },
    initDeliveryPickUpEvent() {
      this.deliveryPickUpEvent = {};

      if (this.pickUpEvents == null) {
        return;
      }

      if (
        this.initialSelection != null &&
        this.initialSelection.EventId != null
      ) {
        // Set selection based on prop
        this.initSelectionByProp();
      }

      // In case that deliveryPickUpEvent is not found in list
      if (
        this.deliveryPickUpEvent == null ||
        Object.keys(this.deliveryPickUpEvent).length === 0
      ) {
        // Set selection with the first Delivery / PickUp event
        this.initSelectionByList();
      }
    },
    initSelectionByProp() {
      // Set selection based on prop
      const eventId = this.initialSelection.EventId;

      switch (this.initialSelection.Type) {
        case 'Shiplee':
        case 'Delivery':
          break;
        case 'PickUp': {
          if (this.pickUpEvents != null && this.pickUpEvents.length > 0) {
            const index = this.pickUpEvents.findIndex(
              (event) => event.EventId === eventId
            );
            if (index >= 0) {
              this.deliveryPickUpEvent = this.pickUpEvents[index];
              this.onDeliveryPickUpChange();
            }
          }
          break;
        }
        default:
          break;
      }
    },
    initSelectionByList() {
      if (this.pickUpEvents != null && this.pickUpEvents.length > 0) {
        [this.deliveryPickUpEvent] = this.pickUpEvents;
        this.onDeliveryPickUpChange();
      } else {
        // Neither DeliveryEvents nor PickUpEvents are available, cannot continue
        this.$emit('error', {
          error: true,
          errorMessage: this.$translate('NO_DELIVERY_OR_PICKUP'),
          errorToRecallFunction: 'getDeliveryAndPickUpEvents',
        });
      }
    },
    initDeliveryFees() {
      this.deliveryFeeItems = [...this.initialDeliveryFees];
      if (this.deliveryFeeItems.length > 0) {
        [this.selectedDeliveryFeeItem] = this.deliveryFeeItems;
      }
      this.onDeliverySelectionChange();
    },
    onDeliverySelectionChange() {
      this.$emit('change:delivery-fee', this.selectedDeliveryFeeItem);
    },
    onDeliveryPickUpChange() {
      this.$emit('change', this.deliveryPickUpEvent);
    },
    isObject(item) {
      return typeof item === 'object';
    },
    getDeliveryPickupSubtitle(item) {
      if (item.Type !== 'PickUp' || item.PickUpFee == null) {
        return '';
      }

      const pickupFeeObj = new PickupFeeJS(item.EventId);
      pickupFeeObj.updateWithRawData(item.PickUpFee);
      return pickupFeeObj.orderValueInfoText();
    },
  },
};
</script>
